import React from 'react';
import { Paper, Stack, Typography, Button, Chip, Box, CircularProgress, Alert } from '@mui/material';
import DevicesIcon from '@mui/icons-material/Devices';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import {
  ASSIGNMENT_STATUS_LABELS,
  computeAssignmentStatus,
  isAssignmentStale,
  summarizePanelAssignmentStatuses,
} from '../../../services/touchPanelDeployable/assignmentStatus';

/**
 * Compact assignment summary for the designer header area.
 */
export default function AssignmentSummaryCard({
  panel,
  assignments = [],
  devicePresence = {},
  onAssignDevices,
  onPrepareDeployment,
  preparing = false,
  onDeploy,
  deploying = false,
  saveStatus = null,
  deployMessage = null,
  deployError = null,
  deploySeverity = 'info',
  onClearDeployFeedback = null,
}) {
  const active = (assignments || []).filter((a) => a && a.enabled !== false);
  const staleCount = active.filter((a) => isAssignmentStale(a, panel?.revision)).length;
  const summary = summarizePanelAssignmentStatuses({
    assignments: active,
    panelRevision: panel?.revision,
  });
  const busy = preparing || deploying;
  // Deploy always allowed when devices are assigned — handler prepares then delivers.
  const canDeploy = active.length > 0;
  const offlineCount = active.filter((a) => devicePresence[a.deviceId] === 'offline').length;

  return (
    <Paper sx={{ p: 1.5 }}>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Assignments</Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            {saveStatus || null}
            <Chip
              size="small"
              icon={<DevicesIcon />}
              label={`${active.length} device${active.length === 1 ? '' : 's'}`}
            />
          </Stack>
        </Stack>

        <Typography variant="caption" color="text.secondary" display="block">
          Rev {panel?.revision ?? '—'}
          {staleCount > 0 ? ` · ${staleCount} stale` : ''}
          {offlineCount > 0 ? ` · ${offlineCount} offline` : ''}
          {summary ? ` · ${summary}` : ''}
        </Typography>

        {active.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No devices assigned yet.
          </Typography>
        ) : (
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {active.slice(0, 6).map((a) => {
              const status = computeAssignmentStatus({
                assignment: a,
                panelRevision: panel?.revision,
              });
              const presence = devicePresence[a.deviceId];
              const presenceLabel =
                presence === 'online' ? 'online' : presence === 'offline' ? 'OFFLINE' : null;
              return (
                <li key={a.id || a.deviceId}>
                  <Typography variant="caption">
                    {a.deviceSnapshot?.name || a.deviceId}{' '}
                    <Typography
                      component="span"
                      variant="caption"
                      color={presence === 'offline' ? 'error.main' : 'text.secondary'}
                    >
                      (
                      {presenceLabel ? `${presenceLabel} · ` : ''}
                      {ASSIGNMENT_STATUS_LABELS[status] || status})
                    </Typography>
                  </Typography>
                </li>
              );
            })}
            {active.length > 6 ? (
              <Typography variant="caption" color="text.secondary">
                +{active.length - 6} more
              </Typography>
            ) : null}
          </Box>
        )}

        {deployError ? (
          <Alert
            severity="error"
            sx={{ py: 0, whiteSpace: 'pre-line' }}
            onClose={onClearDeployFeedback || undefined}
          >
            {deployError}
          </Alert>
        ) : null}
        {deployMessage && !deployError ? (
          <Alert
            severity={deploySeverity === 'success' ? 'success' : deploySeverity === 'warning' ? 'warning' : 'info'}
            sx={{ py: 0, whiteSpace: 'pre-line' }}
            onClose={onClearDeployFeedback || undefined}
          >
            {deployMessage}
          </Alert>
        ) : null}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" onClick={onAssignDevices} disabled={busy}>
            Assign
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={onPrepareDeployment}
            disabled={active.length === 0 || busy}
            startIcon={preparing ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {preparing ? 'Preparing…' : 'Prepare'}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={
              deploying ? <CircularProgress size={14} color="inherit" /> : <RocketLaunchIcon />
            }
            onClick={onDeploy}
            disabled={!canDeploy || busy}
          >
            {deploying ? 'Deploying…' : 'Deploy'}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Deploy saves, prepares, and sends to all assigned panels. Offline panels will not apply
          until they reconnect.
        </Typography>
      </Stack>
    </Paper>
  );
}
